<script setup lang="ts">
import type { Component } from 'vue'
import AccordeonElementalsList from '../../shared/AccordeonElementalsList.vue'
import IconBox from '../../shared/IconBox.vue'
import ElementalItem from '../ElementalItem/index.vue'
import { ElementalListContext, useElementalListContext } from './ElementalListContext'
import ElementalList from './index.vue'
import Footer from '@/components/Elemental/ElementalHeaderInfos/index.vue'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import type { Elemental, ItemsIds } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'
import { useListElementalsQuery } from '~~/src/composables/query/useListElementalsQuery'
import { getElementalEnumByString } from '~~/src/utils/elementals'
import { useUpdateElemental } from '~~/src/composables/mutations/elemetal/useUpdateElemental'
import DraggableElement from '@/components/utils/DragAndDrop/DraggableElement.vue'
import { moveTo } from '~~/src/utils/lists'
import { useSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useSaveElementalInAList'
import { useRemoveSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useRemoveSaveElementalInAList'
import { useSidebarStore } from '~~/src/stores/sidebar'

const props = defineProps<{
  list: Elemental | List
  title?: string
  elementalsType?: string
  iconComponent?: Component
  level?: number
  dataIndex?: number
}>()
const user = useUser()

defineOptions({
  name: 'ElementalList',
})

const toast = useToast()
const { isElementalOpen, toggleElementOpen } = useSidebarStore()

const { listId: parentListId } = useElementalListContext()
const listId = props.list.id
const isOpen = isElementalOpen(listId, parentListId
  ? `${parentListId}`
  : null)
const isOwner = computed(() => {
  return user.value?.id === props.list.user.id
})

const { data: elementals, refetch, isLoading, isRefetching, isError, error } = useListElementalsQuery(
  {
    id: props.list.id,
    query: {
      size: 100,
    },
  }, {
    enabled: isOpen,
    retry: 2,
  },
)

const { mutate: saveElementalInAListMutate, isPending: isPendingSaveElemental } = useSaveElementalInAList()
const { mutate: removeSaveElementalMutate, isPending: isPendingRemoveSaveElemental } = useRemoveSaveElementalInAList()

const { state, countList } = useElementalListsStore()

const elemetalsFiltred = computed(
  () => {
    if (!elementals.value)
      return elementals.value
    if (!state.query || state.query === '')
      return elementals.value
    return elementals.value.filter(items =>
      items.title.toLowerCase().includes(state.query.toLowerCase())
      || items.id.toString().toLowerCase().includes(state.query.toLowerCase()),

    )
  },
)
watch(() => [state.query, elemetalsFiltred.value] as const, ([query, data]) => {
  if (data) {
    const list = props.list
    let count = data.length
    if (query.trim() === '' || props.title?.includes(query) || `${props.list.id}`.includes(query))
      count++

    countList(list.id, list.elementalTypeId!, count)
  }
}, { immediate: true })

const showList = computed(() => state.query === '' || (elemetalsFiltred.value && elemetalsFiltred.value?.length > 0) || props.list.title.toLowerCase().includes(state.query.toLowerCase()))

const { mutate, isPending } = useUpdateElemental()

const moveElemental = (from: number, to: number) => {
  if (!elementals.value)
    return
  if (from === to)
    return
  const items = elementals.value.map((item) => {
    if (item.isList)
      return { listItemId: Number(item.id) }
    return { promptId: item.id }
  })

  const itensToSave = moveTo(items, from, to)
  mutate({
    id: props.list.id,
    elementalType: ElementalTypeEnum.List,
    elemental: {
      title: props.list.title,
      isPremium: props.list.isPremium,
      itemIds: itensToSave,
    },
  }, {
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

onUnmounted(() => {
  const list = props.list
  countList(list.id, list.elementalTypeId!, 0)
})

const validDrop = (data: Elemental) => {
  if (user.value?.id !== props.list.user.id) {
    toast.error('You are not the owner of this list')
    return false
  }
  if (data.isList && data.id === listId) {
    toast.error('You cannot drop a list inside itself')
    return false
  }
  return true
}

const onDrop = (data: { elemental: Elemental; listId?: number }, index: number) => {
  const elemental = data.elemental

  if (!validDrop(elemental))
    return

  const haveListAsParent = data.listId !== undefined
  const elementalType = elemental.isList ? ElementalTypeEnum.List : elemental.elementalTypeId as ElementalTypeEnum

  if (haveListAsParent) {
    removeSaveElementalMutate({
      id: elemental.id,
      listId: `${data.listId}`,
      elementalType,
    }, {
      onError: (error) => {
        toast.error(error.message)
      },
    })
  }

  if (!elementals.value) {
    saveElementalInAListMutate({
      id: elemental.id,
      elementalType,
      listId: props.list.id,
      orderIndex: index >= 0 ? index : undefined,
    }, {
      onSuccess: () => {
        refetch()
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })
    return
  }
  const isList = elemental.isList
  const arrayKey = isList ? 'listItemId' : 'promptId'
  const isOnList = elementals.value?.some(el => el.id === elemental.id)
  if (isOnList)
    return

  const items = elementals.value.map((item) => {
    if (item.isList)
      return { listItemId: Number(item.id) }
    return { promptId: item.id }
  })

  const newElemental = { [arrayKey]: isList ? Number(elemental.id) : elemental.id } as ItemsIds[0]
  const newElementals = insertAtBefore(items, index, newElemental)

  mutate({
    id: props.list.id,
    elementalType: ElementalTypeEnum.List,
    elemental: {
      title: props.list.title,
      isPremium: props.list.isPremium,
      itemIds: newElementals,
    },
  }, {
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

const isLoadingList = computed(() => isLoading.value || isPending.value || isPendingSaveElemental.value || isPendingRemoveSaveElemental.value || isRefetching.value)

provide(ElementalListContext, {
  listId,
  onList: true,
  isOwner,
  parentListId,
  isFolder: true,
})

const folderLevel = props.level ? props.level + 1 : 1
</script>

<template>
  <DraggableElement
    :is="folderLevel === 1 ? 'div' : 'li'" class="list-none" :data="{
      listId: parentListId,
      elemental: list,
    }" :text-content="list.template ?? list.title" :data-index="dataIndex"
  >
    <AccordeonElementalsList
      v-show="showList" :list="list" :list-id="listId" :is-main="false"
      :parent-list-id="parentListId"
      :list-title="title ?? list.title" :n-prompts="elemetalsFiltred ? elemetalsFiltred.length : list.nPrompts ?? 0"
      :elementals-type="getElementalEnumByString(elementalsType)"
      class="aria-expanded:border-1 border-gray-2 aria-expanded:mb-3" @moved="moveElemental" @droped="onDrop"
      @on-toggle="(value) => value && refetch()"
    >
      <template #icon>
        <slot name="list-icon">
          <IconBox>
            <IconFolder w-4 h-4 />
          </IconBox>
        </slot>
      </template>
      <template #content="{ isHover }">
        <template v-if="!isLoadingList">
          <template v-for="(elemental, index) in elemetalsFiltred" :key="elemental.id">
            <ElementalItem
              v-if="!elemental.isList || folderLevel + 1 >= 3" :elemental="elemental"
              :data-index="index"
            />
            <ElementalList
              v-else :list="elemental" :level="folderLevel" :is-main="false"
              :elementals-type="elemental.elementalType" :data-index="index"
            />
          </template>
          <template v-if="!isHover && (!elemetalsFiltred || elemetalsFiltred.length === 0)">
            <div class="text-darkGray text-sm text-center p-4  border-1 border-grayLighter rounded-lg">
              {{ isError ? error?.message : 'no items' }}
            </div>
          </template>
        </template>
        <div v-else class="flex flex-col gap-.5">
          <template v-for="(_, index) in Array(Math.max(Math.min(5, list.nPrompts ?? 3), 1))" :key="index">
            <div
              class="text-darkGray text-sm text-center p-4 h-9 bg-grayLighter  border-1 border-grayLighter rounded-lg animate-pulse"
            />
          </template>
        </div>
      </template>
      <template #footer>
        <Footer :elemental="list" :elemetals-filtred="elemetalsFiltred" />
      </template>
    </AccordeonElementalsList>
  </DraggableElement>
</template>
